import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgInviteBulkManager } from "../../../hooks/OrgInvite/OrgInviteBulk/useOrgInviteBulkManager";
import { useOrgInviteBulkCsvParser } from "../../../hooks/OrgInvite/OrgInviteBulk/useOrgInviteBulkCsvParser";
import { useOrgParams } from "../../../hooks/Org/useOrgParams";
import { OrgInviteBulkDropzone } from "./OrgInviteBulkDropzone";
import { OrgInviteBulkErrors } from "./OrgInviteBulkErrors";
import { OrgInviteBulkTable } from "./OrgInviteBulkTable";
import styles from "./OrgInviteBulkInput.module.css";
import tableStyles from "./OrgInviteBulkTable.module.css";

export function OrgInviteBulkInput() {
  const { category, id } = useOrgParams();
  const queryClient = useQueryClient();
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const {
    pendingInvites,
    parseErrors,
    isCreatingInvitations,
    creationResult,
    setPendingData,
    createBulkInvitations,
    clearData,
    removeInvite,
    hasData,
    hasErrors,
  } = useOrgInviteBulkManager();

  const { parseFile, isProcessing } = useOrgInviteBulkCsvParser();

  const handleFileSelected = async (file: File): Promise<void> => {
    // Clear any previous submission errors when selecting a new file
    setSubmissionError(null);

    const result = await parseFile(file);

    if (result.success && result.data) {
      setPendingData(result.data, []);
    } else {
      setPendingData([], result.errors || ["Unknown parsing error"]);
    }
  };

  const handleSendInvitations = async () => {
    setSubmissionError(null);

    try {
      const result = await createBulkInvitations({
        orgId: id,
        orgCategory: category,
        deliveryMethod: "email",
      });

      if (result) {
        // Invalidate the invitations query to refresh the table
        queryClient.invalidateQueries({
          queryKey: ["orgs", category, id, "invitations"],
        });

        // Clear data after successful submission (only if no failures)
        if (result.failed.length === 0) {
          clearData();
        }
      }
    } catch (_error) {
      // Surface error to user in a friendly way
      setSubmissionError(
        "Failed to send invitations. Please check your connection and try again."
      );
    }
  };

  return (
    <div className={styles.bulkInputContainer}>
      <OrgInviteBulkDropzone
        onFileSelected={handleFileSelected}
        isProcessing={isProcessing || isCreatingInvitations}
      />

      {hasErrors && <OrgInviteBulkErrors errors={parseErrors} />}

      {submissionError && (
        <div className={tableStyles.errorContainer}>
          <p className={tableStyles.errorMessage}>❌ {submissionError}</p>
        </div>
      )}

      {creationResult && (
        <div className={tableStyles.resultsContainer}>
          <h4 className={tableStyles.resultsTitle}>Invitation Results:</h4>
          {creationResult.succeeded.length > 0 && (
            <p className={tableStyles.successMessage}>
              ✅ {creationResult.succeeded.length} invitations sent successfully
            </p>
          )}
          {creationResult.failed.length > 0 && (
            <div>
              <p className={tableStyles.failureMessage}>
                ❌ {creationResult.failed.length} invitations failed:
              </p>
              <ul className={tableStyles.failureList}>
                {creationResult.failed.map((failed, index) => (
                  <li key={index} className={tableStyles.failureItem}>
                    <strong>{failed.email}</strong>: {failed.error_message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {hasData && (
        <OrgInviteBulkTable
          pendingInvites={pendingInvites}
          onRemoveInvite={removeInvite}
          onClearAll={clearData}
          onSendInvitations={handleSendInvitations}
        />
      )}
    </div>
  );
}
